/**
 * Find my size: matches the shopper to a size of the piece she is looking at, or to
 * a size of every piece of a look at once.
 *
 * Two ways in. She gives her height and weight, and her age if she likes, and her
 * bust, waist and hips are estimated from them; that is enough almost every time, so
 * it comes first. Or, to be sure, she types from a tape measure the ones that decide
 * the piece, reached from a link under the first screen or from its result. Either way the result is compared with the body ranges of the product's
 * custom.size_chart (docs/size-chart.md), and the size whose ranges sit closest on
 * the measurements that matter for that piece wins.
 *
 * A shoe is matched on her foot length instead, heel to longest toe, against the
 * foot each size fits.
 *
 * Nothing leaves the browser. The answers are kept in localStorage, so the next
 * piece or look she opens already knows her and she never types the same numbers
 * twice.
 */

/**
 * The estimate. A circumference grows roughly with the square root of weight per
 * unit of height, because weight per unit of height behaves like a cross section.
 * Each measurement has its own exponent because the three do not grow at the same
 * pace: the waist gains fastest and the hip slowest. The reference body and the
 * exponents are calibrated against standard UK sizing, so that 165cm and 62kg lands
 * on a UK 10 to 12, 55kg on a UK 8, 77kg on a UK 16 and 90kg on a UK 20.
 *
 * It is an average. A real body can sit 4cm either side of it, which is why every
 * estimated result offers the tape measure as the closer answer.
 */
const REFERENCE = { weightPerHeight: 62 / 165, bust: 88, waist: 72, hip: 97 };
const EXPONENT = { bust: 0.55, waist: 0.63, hip: 0.47 };

/** Centimetres gained per year of age past 30, at the same height and weight. */
const AGE_SLOPE = { bust: 0.05, waist: 0.1, hip: 0.03 };
const AGE_EFFECT = [-1.5, 6];

/** How far a fit preference moves her measurements before the match, in cm. */
const FIT_SHIFT = { closer: -2, regular: 0, relaxed: 3 };

/**
 * How much worse a size that is too small is than one that is too big, per
 * centimetre. A dress with no stretch that will not close is a return; one with a
 * little room to spare is still a dress she wears.
 */
const TOO_SMALL = { none: 2, low: 1.5, high: 1 };

/** Weight of each deciding measurement, in the order of the chart's keys. */
const KEY_WEIGHT = [1, 0.7, 0.5, 0.3];

/** Centimetres from the edge of a range that count as being between two sizes. */
const EDGE = 1;

/** Centimetres past the smallest or the largest size before she is told so. */
const BEYOND = 2;

/**
 * The same two for a foot, whose sizes are half a centimetre apart: a centimetre
 * would put every foot at the edge of its size and past the grid.
 */
const EDGE_BY_KEY = { foot: 0.15 };
const BEYOND_BY_KEY = { foot: 0.3 };

const LIMITS = {
  height: [135, 210],
  weight: [35, 200],
  age: [13, 100],
  bust: [60, 170],
  waist: [45, 160],
  hip: [65, 180],
  foot: [18, 32],
};

const TAPE_KEYS = ['bust', 'waist', 'hip'];
const CM_PER_INCH = 2.54;
const KG_PER_POUND = 0.45359237;
const STORAGE_KEY = 'dearlova:size-finder';

/**
 * @typedef {object} ChartSize
 * @property {string} size - The value of the product's Size option.
 * @property {Record<string, number | number[]>} [body] - Body ranges in cm.
 * @property {Record<string, number | number[]>} [garment] - Garment measurements in cm.
 */

/**
 * @typedef {object} Chart
 * @property {'none' | 'low' | 'high'} [stretch]
 * @property {string[]} [keys] - Deciding measurements, most important first.
 * @property {ChartSize[]} sizes
 */

/**
 * @typedef {object} Recommendation
 * @property {number} index - Position of the size in the chart.
 * @property {string} size - The size to buy.
 * @property {string[]} keys - Measurements the match was made on.
 * @property {'up' | 'down' | null} edge - She sits near the top or the bottom of it.
 * @property {'over' | 'under' | null} beyond - She is past the largest or smallest size.
 * @property {string | null} next - The size above, if there is one.
 * @property {string | null} previous - The size below, if there is one.
 */

/**
 * @typedef {object} Measured
 * @property {Record<string, number>} body - Her measurements in cm.
 * @property {boolean} estimated - Whether they came from height and weight.
 * @property {number | null} [foot] - Her foot length in cm, when a piece is a shoe.
 */

/**
 * @param {number} value
 * @param {number[]} range
 */
function clamp(value, [min, max]) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Estimates bust, waist and hips from height and weight.
 *
 * @param {number} height - In cm.
 * @param {number} weight - In kg.
 * @param {number | null} [age]
 * @returns {Record<string, number>} Measurements in cm, height included.
 */
export function estimateBody(height, weight, age = null) {
  const ratio = weight / height / REFERENCE.weightPerHeight;
  /** @type {Record<string, number>} */
  const body = { height };

  for (const key of TAPE_KEYS) {
    const ageEffect = age ? clamp(AGE_SLOPE[key] * (age - 30), AGE_EFFECT) : 0;
    body[key] = REFERENCE[key] * ratio ** EXPONENT[key] + ageEffect;
  }

  return body;
}

/**
 * @param {number | number[] | undefined} value
 * @returns {[number, number] | null}
 */
function toRange(value) {
  if (Array.isArray(value) && value.length) return [value[0], value[value.length - 1]];
  if (typeof value === 'number') return [value, value];
  return null;
}

/**
 * Picks the size whose body ranges sit closest to her measurements.
 *
 * Only the chart's deciding measurements count, weighted in the order the chart
 * lists them. Being bigger than a size costs more than being smaller than it, by
 * how little the fabric gives. When two sizes both fit, the one she sits nearer the
 * middle of wins, and a fit preference moves her measurements before any of it.
 *
 * @param {Chart} chart
 * @param {Record<string, number>} measured - Measurements in cm.
 * @param {keyof typeof FIT_SHIFT} [fit]
 * @returns {Recommendation | null}
 */
export function recommendSize(chart, measured, fit = 'regular') {
  const sizes = chart?.sizes ?? [];
  const wanted = chart?.keys?.length ? chart.keys : TAPE_KEYS;
  const keys = wanted.filter(
    (key) => measured[key] != null && sizes.every((size) => toRange(size.body?.[key]))
  );
  if (!sizes.length || !keys.length) return null;

  const shift = FIT_SHIFT[fit] ?? 0;
  /** @type {Record<string, number>} */
  const body = {};
  // A fit preference is room in the cloth; a foot is the foot.
  for (const key of keys) body[key] = measured[key] + (key === 'height' || key === 'foot' ? 0 : shift);

  const tooSmall = TOO_SMALL[chart.stretch ?? 'none'] ?? TOO_SMALL.none;

  // A foot on the line between two sizes gets the larger: a shoe that pinches
  // goes back, one with a little room gets worn.
  const roomier = keys.includes('foot');
  /** @param {number} x @param {number} y */
  const tie = (x, y) => (Math.abs(x - y) < 1e-9 ? 0 : x - y);

  const ranked = sizes
    .map((size, index) => {
      let penalty = 0;
      let offCentre = 0;

      keys.forEach((key, position) => {
        const [min, max] = /** @type {[number, number]} */ (toRange(size.body?.[key]));
        const weight = KEY_WEIGHT[position] ?? KEY_WEIGHT[KEY_WEIGHT.length - 1];
        const value = body[key];

        if (value > max) penalty += (value - max) * tooSmall * weight;
        else if (value < min) penalty += (min - value) * weight;
        offCentre += Math.abs(value - (min + max) / 2) * weight;
      });

      return { index, penalty, offCentre };
    })
    .sort((a, b) => a.penalty - b.penalty || tie(a.offCentre, b.offCentre) || (roomier ? b.index - a.index : 0));

  const index = ranked[0].index;
  const last = sizes.length - 1;
  const ranges = keys.map((key) => /** @type {[number, number]} */ (toRange(sizes[index].body?.[key])));

  // Room left on her tightest measurement, and give on her loosest.
  const room = Math.min(...keys.map((key, i) => ranges[i][1] - body[key]));
  const give = Math.max(...keys.map((key, i) => body[key] - ranges[i][0]));

  const edgeLimit = Math.min(...keys.map((key) => EDGE_BY_KEY[key] ?? EDGE));
  const beyondLimit = Math.min(...keys.map((key) => BEYOND_BY_KEY[key] ?? BEYOND));

  /** @type {Recommendation['beyond']} */
  let beyond = null;
  if (index === last && room < -beyondLimit) beyond = 'over';
  else if (index === 0 && give < -beyondLimit) beyond = 'under';

  /** @type {Recommendation['edge']} */
  let edge = null;
  if (!beyond) {
    if (index < last && room < edgeLimit) edge = 'up';
    else if (index > 0 && give < edgeLimit) edge = 'down';
  }

  return {
    index,
    size: sizes[index].size,
    keys,
    edge,
    beyond,
    next: sizes[index + 1]?.size ?? null,
    previous: sizes[index - 1]?.size ?? null,
  };
}

/**
 * @param {FormDataEntryValue | null | undefined} value
 * @returns {number | null}
 */
function toNumber(value) {
  const number = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(number) && number > 0 ? number : null;
}

/**
 * @param {string} template
 * @param {Record<string, string | null | undefined>} values
 */
function fill(template, values) {
  return (template ?? '').replace(/\[(\w+)\]/g, (match, name) => values[name] ?? match);
}

/**
 * @param {Element} root
 * @param {string} selector
 */
function readJson(root, selector) {
  try {
    return JSON.parse(root.querySelector(selector)?.textContent ?? '');
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, FormDataEntryValue>} answers
 * @returns {keyof typeof FIT_SHIFT}
 */
function fitOf(answers) {
  const fit = String(answers.fit ?? 'regular');
  return fit in FIT_SHIFT ? /** @type {keyof typeof FIT_SHIFT} */ (fit) : 'regular';
}

/**
 * Chooses a value in the theme's own option controls, buttons or dropdown, found by
 * the option's name. The change bubbles from the control itself, so whatever owns
 * those controls, the product page's picker or a look's builder, reacts exactly as
 * if the shopper had chosen it.
 *
 * @param {ParentNode} root
 * @param {string} optionName
 * @param {string} value
 */
function chooseOption(root, optionName, value) {
  const name = optionName.trim().toLowerCase();

  for (const fieldset of root.querySelectorAll('fieldset')) {
    const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() ?? '';
    if (!legend.startsWith(name)) continue;

    for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement && input.value === value && !input.checked) input.click();
    }
  }

  for (const select of root.querySelectorAll('select')) {
    const label = root.querySelector(`label[for="${CSS.escape(select.id)}"]`)?.textContent?.trim().toLowerCase();
    if (!label?.startsWith(name) || select.value === value) continue;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * What the product page and the look share: reading the form, turning the answers
 * into measurements, remembering them, and the result's sentences. The elements
 * below decide only what the answer is and what pressing it does.
 *
 * Not a theme Component: everything it touches lives inside the dialog, and the
 * dialog belongs to the theme's own dialog-component, which would claim any refs
 * placed there. Plain queries keep the two from competing.
 */
class SizeFinderBase extends HTMLElement {
  /** @type {Record<string, string>} */
  copy = {};

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLElement | null} */
  result = null;

  /** @type {HTMLElement | null} */
  dialog = null;

  #hasSavedAnswers = false;

  connectedCallback() {
    this.copy = readJson(this, '[data-size-finder-copy]') ?? {};
    this.form = this.querySelector('[data-size-finder-form]');
    this.result = this.querySelector('[data-size-finder-result]');
    this.dialog = this.querySelector('dialog-component');

    if (!this.readData() || !this.form || !this.result) return;

    this.form.addEventListener('submit', this.#onSubmit);
    this.form.addEventListener('click', this.#onFormClick);
    this.result.addEventListener('click', this.#onResultClick);

    // The tape screen asks only for what decides these pieces.
    const keys = this.#tapeKeys;
    for (const field of this.form.querySelectorAll('[data-tape-key]')) {
      if (field instanceof HTMLElement) field.hidden = !keys.includes(field.dataset.tapeKey ?? '');
    }

    // A shoe asks for her foot length. When shoes are all there is to size, that is
    // the whole form, and height, weight and fit step aside.
    const shoeField = this.form.querySelector('[data-shoe-field]');
    if (shoeField instanceof HTMLElement) shoeField.hidden = !this.#needsShoe;
    this.form.toggleAttribute('data-shoe-only', this.#shoeOnly);

    this.dialog?.addEventListener('dialog:open', this.#onOpen);
    this.#restore();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.form?.removeEventListener('click', this.#onFormClick);
    this.result?.removeEventListener('click', this.#onResultClick);
    this.dialog?.removeEventListener('dialog:open', this.#onOpen);
  }

  /**
   * Reads the element's own data island.
   *
   * @returns {boolean} Whether there is anything to match against.
   */
  readData() {
    return false;
  }

  /**
   * The charts that decide a size to choose. The tape form asks for what they need
   * and nothing more.
   *
   * @returns {Chart[]}
   */
  get decidingCharts() {
    return [];
  }

  /**
   * Fills the result panel.
   *
   * @param {Measured} measured
   * @param {Record<string, FormDataEntryValue>} answers
   * @returns {boolean} Whether there was an answer to give.
   */
  renderResult(measured, answers) {
    return false;
  }

  /**
   * Reacts to a press inside the result panel, other than Change my answers.
   *
   * @param {Element} target
   */
  handleResultClick(target) {}

  /**
   * The sentence under a size, when she sits near its edge or past the grid.
   *
   * @param {Recommendation} recommendation
   */
  note({ size, edge, beyond, next, previous }) {
    if (beyond === 'over') return fill(this.copy.over, { size });
    if (beyond === 'under') return fill(this.copy.under, { size });
    if (edge === 'up') return fill(this.copy.edge_up, { size, next });
    if (edge === 'down') return fill(this.copy.edge_down, { size, previous });
    return '';
  }

  closeDialog() {
    /** @type {any} */ (this.dialog)?.closeDialog?.();
  }

  /**
   * A shopper who has answered before sees her last answer as soon as the dialog
   * opens. When she last measured herself but this piece is decided by a measurement
   * she never gave, her height and weight answer instead, and if she never gave
   * those either, the tape screen stays open asking for what is missing.
   */
  #onOpen = () => {
    if (!this.#hasSavedAnswers || !this.result?.hidden) return;
    if (this.#match({ quiet: true }) || this.#mode !== 'tape') return;

    this.#setMode('quick');
    if (!this.#match({ quiet: true })) this.#setMode('tape');
  };

  /** @param {MouseEvent} event */
  #onFormClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-switch-mode]') : null;
    if (!(target instanceof HTMLElement)) return;

    this.#setMode(target.dataset.switchMode === 'tape' ? 'tape' : 'quick');
    this.#showError(null);
    this.#focusFirstField();
  };

  get #mode() {
    const checked = this.form?.querySelector('[name="mode"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : 'quick';
  }

  /** @param {'quick' | 'tape'} mode */
  #setMode(mode) {
    const radio = this.form?.querySelector(`[name="mode"][value="${mode}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    this.#match({ quiet: false });
  };

  /** @param {MouseEvent} event */
  #onResultClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-result-again]')) {
      this.#showForm();
      return;
    }

    if (target.closest('[data-result-refine-button]')) {
      this.#setMode('tape');
      this.#showForm();
      return;
    }

    this.handleResultClick(target);
  };

  /**
   * @param {{ quiet: boolean }} options
   * @returns {boolean} Whether an answer is on screen.
   */
  #match({ quiet }) {
    if (!this.form || !this.result) return false;

    const answers = Object.fromEntries(new FormData(this.form));
    const measured = this.#measure(answers);

    if ('error' in measured) {
      if (!quiet) this.#showError(measured.error);
      return false;
    }

    if (!this.renderResult(measured, answers)) {
      if (!quiet) this.#showError(this.copy.error_range);
      return false;
    }

    const basis = this.result.querySelector('[data-result-basis]');
    if (basis) basis.textContent = this.#basis(measured, answers.units === 'imperial');

    this.#showError(null);
    this.form.hidden = true;
    this.result.hidden = false;
    const heading = this.result.querySelector('[data-result-heading]');
    if (heading instanceof HTMLElement) heading.focus();

    // An estimate is offered the tape; her own measurements are offered a change.
    const refine = this.result.querySelector('[data-result-refine]');
    if (refine instanceof HTMLElement) refine.hidden = !measured.estimated;
    const again = this.result.querySelector('[data-result-again]');
    if (again) {
      again.textContent = measured.estimated || this.#shoeOnly ? this.copy.again_quick : this.copy.again_tape;
    }

    // Only what she pressed herself is remembered: the fallback on opening must not
    // turn her saved tape answers into height and weight ones.
    if (!quiet) this.#save(answers);
    return true;
  }

  /** Whether any piece is a shoe, matched on her foot length. */
  get #needsShoe() {
    return this.decidingCharts.some((chart) => chart.keys?.includes('foot'));
  }

  /** Whether shoes are all there is to size, so her foot is the only question. */
  get #shoeOnly() {
    const charts = this.decidingCharts;
    return charts.length > 0 && charts.every((chart) => chart.keys?.length === 1 && chart.keys[0] === 'foot');
  }

  /**
   * Her foot length in cm, or an error when what she typed is not a foot.
   *
   * @param {Record<string, FormDataEntryValue>} answers
   * @returns {number | null | { error: string }}
   */
  #foot(answers) {
    const value = toNumber(answers.foot);
    if (value == null) return null;

    const foot = answers.units === 'imperial' ? value * CM_PER_INCH : value;
    return this.#outOfRange({ foot }) ? { error: this.copy.error_range } : foot;
  }

  /** The deciding measurements a tape can give, which the tape form must ask for. */
  get #tapeKeys() {
    const keys = new Set();
    for (const chart of this.decidingCharts) {
      for (const key of chart.keys?.length ? chart.keys : TAPE_KEYS) {
        if (TAPE_KEYS.includes(key)) keys.add(key);
      }
    }
    return TAPE_KEYS.filter((key) => keys.has(key));
  }

  /**
   * Turns the form's answers into measurements in cm.
   *
   * @param {Record<string, FormDataEntryValue>} answers
   * @returns {Measured | { error: string }}
   */
  #measure(answers) {
    const foot = this.#needsShoe ? this.#foot(answers) : null;
    if (foot != null && typeof foot === 'object') return foot;

    if (this.#shoeOnly) {
      if (foot == null) return { error: this.copy.error_shoe };
      return { body: { foot }, estimated: false, foot };
    }

    // Everywhere else the foot is optional: a look's shoe without it stays at her
    // usual size.
    const measured = this.#measureBody(answers);
    if (!('error' in measured) && foot != null) {
      measured.body.foot = foot;
      measured.foot = foot;
    }
    return measured;
  }

  /**
   * Bust, waist and hips from the form, taped or estimated.
   *
   * @param {Record<string, FormDataEntryValue>} answers
   * @returns {Measured | { error: string }}
   */
  #measureBody(answers) {
    const imperial = answers.units === 'imperial';

    if (answers.mode === 'tape') {
      /** @type {Record<string, number>} */
      const body = {};
      for (const key of TAPE_KEYS) {
        const value = toNumber(answers[key]);
        if (value != null) body[key] = imperial ? value * CM_PER_INCH : value;
      }

      const missing = this.#tapeKeys.filter((key) => body[key] == null);
      if (missing.length) return { error: fill(this.copy.error_tape, { keys: this.#list(missing) }) };
      if (this.#outOfRange(body)) return { error: this.copy.error_range };

      return { body, estimated: false };
    }

    const height = imperial
      ? this.#combine(answers.height_ft, answers.height_in, 12, CM_PER_INCH)
      : toNumber(answers.height_cm);
    const weight = imperial
      ? this.#combine(answers.weight_st, answers.weight_lb, 14, KG_PER_POUND)
      : toNumber(answers.weight_kg);
    const age = toNumber(answers.age);

    if (height == null || weight == null) return { error: this.copy.error_quick };

    /** @type {Record<string, number>} */
    const given = { height, weight };
    if (age != null) given.age = age;
    if (this.#outOfRange(given)) return { error: this.copy.error_range };

    return { body: estimateBody(height, weight, age), estimated: true };
  }

  /**
   * Feet and inches, or stones and pounds, as one metric number.
   *
   * @param {FormDataEntryValue | undefined} large
   * @param {FormDataEntryValue | undefined} small
   * @param {number} smallPerLarge
   * @param {number} metricPerSmall
   */
  #combine(large, small, smallPerLarge, metricPerSmall) {
    const big = toNumber(large);
    const little = toNumber(small);
    if (big == null && little == null) return null;
    return ((big ?? 0) * smallPerLarge + (little ?? 0)) * metricPerSmall;
  }

  /** @param {Record<string, number>} values */
  #outOfRange(values) {
    return Object.entries(values).some(([key, value]) => {
      const limits = LIMITS[/** @type {keyof typeof LIMITS} */ (key)];
      return limits && (value < limits[0] || value > limits[1]);
    });
  }

  /** @param {string[]} keys */
  #list(keys) {
    const names = keys.map((key) => this.copy[`key_${key}`] ?? key);
    if (names.length < 2) return names.join('');
    return `${names.slice(0, -1).join(', ')}${this.copy.list_and}${names[names.length - 1]}`;
  }

  /**
   * @param {number} cm
   * @param {boolean} imperial
   */
  #format(cm, imperial) {
    if (!imperial) return `${Math.round(cm)}cm`;
    return `${Math.round((cm / CM_PER_INCH) * 2) / 2}in`;
  }

  /**
   * @param {Measured} measured
   * @param {boolean} imperial
   */
  #basis({ body, estimated, foot }, imperial) {
    const shoeOnly = this.#shoeOnly;
    const shoeText =
      foot != null
        ? fill(shoeOnly ? this.copy.basis_shoe : this.copy.basis_look_shoe, {
            foot: imperial ? `${Math.round((foot / CM_PER_INCH) * 4) / 4}in` : `${Math.round(foot * 10) / 10}cm`,
          })
        : '';
    if (shoeOnly) return shoeText;

    const bodyText = estimated
      ? fill(this.copy.basis_estimate, {
          bust: this.#format(body.bust, imperial),
          waist: this.#format(body.waist, imperial),
          hip: this.#format(body.hip, imperial),
        })
      : fill(this.copy.basis_tape, { keys: this.#list(this.#tapeKeys.filter((key) => body[key] != null)) });
    return shoeText ? `${bodyText} ${shoeText}` : bodyText;
  }

  #showForm() {
    if (!this.form || !this.result) return;
    this.result.hidden = true;
    this.form.hidden = false;
    this.#focusFirstField();
  }

  /** The first field she can see on the current screen. */
  #focusFirstField() {
    const fields = this.form?.querySelectorAll('input:not([type="radio"])') ?? [];
    const field = [...fields].find((input) => input instanceof HTMLElement && input.offsetParent !== null);
    if (field instanceof HTMLElement) field.focus();
  }

  /** @param {string | null} message */
  #showError(message) {
    const error = this.form?.querySelector('[data-size-finder-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message ?? '';
    error.hidden = !message;
  }

  /** @param {Record<string, FormDataEntryValue>} answers */
  #save(answers) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
      this.#hasSavedAnswers = true;
    } catch {
      // Private windows and blocked storage: she simply types her answers again.
    }
  }

  #restore() {
    /** @type {Record<string, string> | null} */
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      saved = null;
    }
    if (!saved || typeof saved !== 'object' || !this.form) return;

    for (const [name, value] of Object.entries(saved)) {
      const field = this.form.elements.namedItem(name);
      if (field && 'value' in field && typeof value === 'string') field.value = value;
    }
    this.#hasSavedAnswers = true;
  }
}

/**
 * The product page: one chart, one size, and a button that selects it in the page's
 * own picker, so the price, the stock and the add to cart all follow.
 */
class SizeFinderComponent extends SizeFinderBase {
  /** @type {Chart | null} */
  #chart = null;

  readData() {
    this.#chart = readJson(this, '[data-size-finder-chart]');
    return Boolean(this.#chart?.sizes?.length);
  }

  get decidingCharts() {
    return this.#chart ? [this.#chart] : [];
  }

  /**
   * @param {Measured} measured
   * @param {Record<string, FormDataEntryValue>} answers
   */
  renderResult(measured, answers) {
    if (!this.#chart || !this.result) return false;

    const recommendation = recommendSize(this.#chart, measured.body, fitOf(answers));
    if (!recommendation) return false;

    const { size } = recommendation;
    const sizeText = this.result.querySelector('[data-result-size]');
    if (sizeText) sizeText.textContent = size;

    const note = this.result.querySelector('[data-result-note]');
    if (note instanceof HTMLElement) {
      const text = this.note(recommendation);
      note.textContent = text;
      note.hidden = !text;
    }

    const select = this.result.querySelector('[data-result-select]');
    if (select instanceof HTMLElement) {
      select.textContent = fill(this.copy.select, { size });
      select.dataset.size = size;
    }

    return true;
  }

  /** @param {Element} target */
  handleResultClick(target) {
    const select = target.closest('[data-result-select]');
    if (!(select instanceof HTMLElement) || !select.dataset.size) return;

    const picker = document.querySelector(
      `variant-picker[data-product-id="${CSS.escape(this.dataset.productId ?? '')}"][data-template-product-match="true"]`
    );
    if (picker) chooseOption(picker, this.dataset.optionName ?? 'Size', select.dataset.size);
    this.closeDialog();
  }
}

/**
 * @typedef {object} LookPiece
 * @property {number} index - Position of the piece's card in the look.
 * @property {string} title
 * @property {string} option - Name of its Size option, empty when it comes in one size.
 * @property {Chart | null} chart
 */

/**
 * A Shop the Look: one set of answers, a line per piece. A piece with a size to
 * choose and a chart gets its size; a one size piece with a chart gets whether it
 * should fit; a piece with sizes and no measured chart (a shoe without one, or
 * clothing on the estimated standard grade) gets "your usual size", and so does a
 * shoe with a chart when she leaves her foot length out. Apply to my look presses
 * each card's own size.
 */
class LookSizeFinderComponent extends SizeFinderBase {
  /** @type {LookPiece[]} */
  #pieces = [];

  /** @type {{ index: number, option: string, size: string }[]} */
  #picks = [];

  readData() {
    this.#pieces = readJson(this, '[data-size-finder-pieces]') ?? [];
    return this.decidingCharts.length > 0;
  }

  get decidingCharts() {
    return this.#pieces.flatMap((piece) => (piece.option && piece.chart ? [piece.chart] : []));
  }

  /**
   * @param {Measured} measured
   * @param {Record<string, FormDataEntryValue>} answers
   */
  renderResult(measured, answers) {
    const list = this.result?.querySelector('[data-result-list]');
    if (!list) return false;

    /** @type {HTMLLIElement[]} */
    const rows = [];
    /** @type {{ index: number, option: string, size: string }[]} */
    const picks = [];

    for (const piece of this.#pieces) {
      if (!piece.chart) {
        if (piece.option) rows.push(this.#row(piece.title, this.copy.usual_size, ''));
        continue;
      }

      const recommendation = recommendSize(piece.chart, measured.body, fitOf(answers));
      if (!recommendation) {
        // A one size piece decided by a measurement the tape screen did not ask for
        // still belongs in the look; there is just nothing to say about its fit. A
        // shoe she gave no foot length for is left to her usual size.
        rows.push(this.#row(piece.title, piece.option ? this.copy.usual_size : this.copy.one_size, ''));
        continue;
      }

      if (piece.option) {
        rows.push(this.#row(piece.title, recommendation.size, this.note(recommendation)));
        picks.push({ index: piece.index, option: piece.option, size: recommendation.size });
      } else {
        const snug = recommendation.beyond === 'over';
        rows.push(this.#row(piece.title, this.copy.one_size, snug ? this.copy.one_size_snug : this.copy.one_size_fits));
      }
    }

    if (!picks.length) return false;

    this.#picks = picks;
    list.replaceChildren(...rows);
    return true;
  }

  /**
   * @param {string} title
   * @param {string} size
   * @param {string} note
   */
  #row(title, size, note) {
    const row = document.createElement('li');
    row.className = 'size-finder__pick';

    const name = document.createElement('span');
    name.className = 'size-finder__pick-title';
    name.textContent = title;

    const value = document.createElement('span');
    value.className = 'size-finder__pick-size';
    value.textContent = size;

    row.append(name, value);

    if (note) {
      const detail = document.createElement('span');
      detail.className = 'size-finder__pick-note';
      detail.textContent = note;
      row.append(detail);
    }

    return row;
  }

  /** @param {Element} target */
  handleResultClick(target) {
    if (!target.closest('[data-result-apply]')) return;

    const look = this.closest('shop-the-look-component') ?? document;
    for (const pick of this.#picks) {
      const card = look.querySelector(`[data-look-item][data-index="${pick.index}"]`);
      if (card) chooseOption(card, pick.option, pick.size);
    }
    this.closeDialog();
  }
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('size-finder')) customElements.define('size-finder', SizeFinderComponent);
  if (!customElements.get('look-size-finder')) customElements.define('look-size-finder', LookSizeFinderComponent);
}
